import React, { useState, useRef, forwardRef, useEffect } from 'react';
import { Box, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';

import { Input } from 'ui/FormInputs';
import { HelperText } from 'ui';
import styles from './SsnInput.module.css';

const SsnInput = forwardRef(
    (
        {
            disableFirst,
            disableSecond,
            disableAll,
            onChange,
            label,
            isRequired,
            error,
            helperText,
            autoFocus,
        },
        ref
    ) => {
        const [ssn, setSsn] = useState({ first: '', middle: '', last: '' });
        const [inputType, setInputType] = useState({
            first: 'password',
            middle: 'password',
            last: 'password',
        });

        const firstRef = useRef(null);
        const secondRef = useRef(null);
        const thirdRef = useRef(null);

        useEffect(() => {
            if (autoFocus) {
                if (!disableFirst && !disableAll && firstRef.current) {
                    firstRef.current.focus();
                } else if (!disableSecond && !disableAll && secondRef.current) {
                    secondRef.current.focus();
                } else if (!disableAll && thirdRef.current) {
                    thirdRef.current.focus();
                }
            }
        }, [autoFocus, disableFirst, disableSecond, disableAll]);

        const handleChange = (e, field, maxLength, nextRef) => {
            let value = e.target.value;
            // Remove all non-numeric characters
            value = value.replace(/\D/g, '');

            if (value.length > maxLength) {
                value = value.slice(0, maxLength);
            }
            const updatedSsn = { ...ssn, [field]: value };
            setSsn(updatedSsn);
            onChange(`${updatedSsn.first}${updatedSsn.middle}${updatedSsn.last}`);
            if (value.length === maxLength && nextRef) {
                nextRef.current.focus();
            }
        };

        const handleKeyDown = (e, field, prevRef) => {
            if (e.key === 'Backspace' && ssn[field].length === 0 && prevRef) {
                prevRef.current.focus();
            }
        };

        const handleFocusOrBlur = (field, type) => {
            setInputType((prevState) => ({
                ...prevState,
                [field]: type === 'focus' ? 'text' : 'password',
            }));
        };

        return (
            <Box>
                <Typography component="label" variant="inherit" className={styles.label}>
                    {label}
                    {isRequired && '*'}
                </Typography>
                <Box className={styles.input_box_container}>
                    <Input
                        type={inputType.first}
                        placeholder="XXX"
                        maxLength={3}
                        value={ssn.first}
                        onChange={(e) => handleChange(e, 'first', 3, secondRef)}
                        onKeyDown={(e) => handleKeyDown(e, 'first', firstRef)}
                        onFocus={() => handleFocusOrBlur('first', 'focus')}
                        onBlur={() => handleFocusOrBlur('first', 'blur')}
                        inputRef={firstRef}
                        wrapperClass={styles.inputField}
                        disabled={disableFirst || disableAll}
                        error={error}
                    />
                    <span>-</span>
                    <Input
                        type={inputType.middle}
                        placeholder="XX"
                        maxLength={2}
                        value={ssn.middle}
                        onChange={(e) => handleChange(e, 'middle', 2, thirdRef)}
                        onKeyDown={(e) => handleKeyDown(e, 'middle', firstRef)}
                        onFocus={() => handleFocusOrBlur('middle', 'focus')}
                        onBlur={() => handleFocusOrBlur('middle', 'blur')}
                        inputRef={secondRef}
                        wrapperClass={styles.inputField}
                        disabled={disableSecond || disableAll}
                        error={error}
                    />
                    <span>-</span>
                    <Input
                        type={inputType.last}
                        placeholder="XXXX"
                        maxLength={4}
                        value={ssn.last}
                        onChange={(e) => handleChange(e, 'last', 4)}
                        onKeyDown={(e) => handleKeyDown(e, 'last', secondRef)}
                        onFocus={() => handleFocusOrBlur('last', 'focus')}
                        onBlur={() => handleFocusOrBlur('last', 'blur')}
                        inputRef={thirdRef}
                        wrapperClass={styles.inputField}
                        disabled={disableAll}
                        error={error}
                    />
                </Box>
                {helperText && <HelperText>{helperText}</HelperText>}
            </Box>
        );
    }
);

SsnInput.propTypes = {
    disableFirst: PropTypes.bool,
    disableSecond: PropTypes.bool,
    disableAll: PropTypes.bool,
    onChange: PropTypes.func,
    label: PropTypes.string,
    isRequired: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    autoFocus: PropTypes.bool,
};

export default SsnInput;
